// Сборка набора TipTap-расширений: схема СТО (core/schema) + служебные плагины ядра + расширения функций
// (registry.extensions) + сочетания клавиш команд (registry.commands[].shortcut).
import { Extension, type AnyExtension } from '@tiptap/core';
import { Placeholder, UndoRedo, Dropcursor, Gapcursor } from '@tiptap/extensions';
import { baseExtensions } from '../schema/base';
import { structureExtensions } from '../schema/structure';
import { listExtensions } from '../schema/lists';
import { objectExtensions } from '../schema/objects';
import { mathExtensions } from '../schema/math';
import { noteExtensions } from '../schema/notes';
import { inlineExtensions } from '../schema/inline';
import { NumberingExtension } from './numberingPlugin';
import { NormalizeExtension } from './normalize';
import { PasteExtension } from './paste';
import { registry } from '../registry';
import { runCommand } from '../ctx';

const HEADING_PH = ['', 'Название раздела', 'Название подраздела', 'Название пункта'];
const BODY_PH: Record<string, string> = {
  intro: 'Текст введения: актуальность темы, цель и задачи работы…',
  conclusions: 'Выводы: что сделано и какие результаты получены…',
  conclusion: 'Заключение: итоги работы, достигнута ли цель…',
  terms: 'Термин – определение…',
  abbrev: 'Сокращение – расшифровка…',
  defs: 'Обозначение – расшифровка…',
  normrefs: 'Ссылки на стандарты…',
};
const CAPTION_PH: Record<string, string> = { figure: 'Название рисунка', stoTable: 'Название таблицы', listing: 'Название листинга' };

function placeholderText({ node, pos, editor }: { node: any; pos: number; editor: any }): string {
  const t = node.type.name;
  if (t === 'heading') return HEADING_PH[node.attrs.level] || '';
  if (t === 'appendixTitle') return 'Название приложения';
  if (t === 'caption') {
    try { return CAPTION_PH[editor.state.doc.resolve(pos).parent.type.name] || 'Название'; } catch { return 'Название'; }
  }
  if (t === 'paragraph' && editor.state.doc.childCount <= 1) return 'Начните писать отчёт…';
  // пустой абзац сразу под заголовком - подсказка, что здесь писать (новичку в шаблоне «Структура по СТО»)
  if (t === 'paragraph') {
    try {
      const $p = editor.state.doc.resolve(pos);
      const i = $p.index();
      const prev = i > 0 ? $p.parent.child(i - 1) : null;
      if (prev?.type.name === 'structHeading') return BODY_PH[prev.attrs.kind] || 'Текст…';
      if (prev?.type.name === 'heading') return 'Текст раздела…';
    } catch { /* позиция вне документа */ }
  }
  return '';
}

/** Сочетания клавиш из реестра команд. */
function shortcutsExtension() {
  return Extension.create({
    name: 'stoShortcuts',
    priority: 900,
    addKeyboardShortcuts() {
      const map: Record<string, () => boolean> = {};
      for (const c of registry.commands.list()) {
        if (c.shortcut) map[c.shortcut] = () => { runCommand(c.id); return true; };
      }
      return map;
    },
  });
}

export function buildExtensions(): AnyExtension[] {
  const core: AnyExtension[] = [
    ...baseExtensions(), ...structureExtensions(), ...listExtensions(), ...objectExtensions(),
    ...mathExtensions(), ...noteExtensions(), ...inlineExtensions(),
    UndoRedo.configure({ depth: 500 }),
    Dropcursor.configure({ color: '#1f5fa8', width: 2 }),
    Gapcursor,
    Placeholder.configure({ placeholder: placeholderText as any, includeChildren: true, showOnlyCurrent: false }),
    NumberingExtension, NormalizeExtension, PasteExtension, shortcutsExtension(),
  ];
  const extra = registry.extensions.list().flatMap((s) => { const r = s.create(); return Array.isArray(r) ? r : [r]; });
  return [...core, ...extra];
}
